import { useEffect, useMemo } from 'react'
import { isEmpty, last } from 'lodash-es'
import type { RouteSegment, StreetNodePlanningData, StreetSegmentPlanningData } from '@/api/main/types.gen'
import { computeGapFixes, detectRouteGapNodeIds, type GapFix } from './gapFix'
import { buildHintVisitNumbers, hintReconnectVisitNumber } from './nodeLabels'
import { computeRemovalPlan, type RemovalPlan } from './removalRange'
import { findTailReconnectIndex } from './routePlanUtils'
import { buildRoutingGraph, findShortestPath, findShortestPathToFirstReachable } from './shortestPath'
import type { EditState, PathState, PendingRemoval } from './types'

type UsePlannerHintsAndGapsArgs = {
  isDeleteMode: boolean
  isPathBuildMode: boolean
  pathState: PathState
  pathNodes: string[]
  editState: EditState | null
  addTarget: string | null
  pendingRemoval: PendingRemoval | null
  setGapFixPreview: (preview: boolean) => void
  nonBlockedStreetSegments: StreetSegmentPlanningData[]
  indexedSegments: Record<string, StreetSegmentPlanningData>
  streetNodes: Record<string, StreetNodePlanningData>
}

/**
 * Purely derived planner data (hints, removal plans, gap fixes). All transient
 * state (add target, pending removal, preview flag) lives in the planner core.
 */
export function usePlannerHintsAndGaps({
  isDeleteMode,
  isPathBuildMode,
  pathState,
  pathNodes,
  editState,
  addTarget,
  pendingRemoval,
  setGapFixPreview,
  nonBlockedStreetSegments,
  indexedSegments,
  streetNodes,
}: UsePlannerHintsAndGapsArgs) {
  const routingGraph = useMemo(() => buildRoutingGraph(nonBlockedStreetSegments), [nonBlockedStreetSegments])

  const removalPlan: RemovalPlan | null = useMemo(() => {
    if (!isDeleteMode || !pendingRemoval) return null
    return computeRemovalPlan(pendingRemoval, pathNodes, routingGraph)
  }, [isDeleteMode, pendingRemoval, pathNodes, routingGraph])

  const shortestPathHint = useMemo((): RouteSegment[] | null => {
    if (isDeleteMode) {
      if (removalPlan?.status === 'ready' && removalPlan.mode === 'reconnect') {
        return removalPlan.replacement
      }
      return null
    }
    if (!editState) {
      if (!addTarget || !pathState.lastNode || pathState.lastNode === addTarget) return null
      const segs = findShortestPath(routingGraph, pathState.lastNode, addTarget)
      return !isEmpty(segs) ? segs! : null
    }
    if (!pathState.lastNode) return null
    const lastNodeId = pathState.lastNode
    const tail = editState.originalTail
    const tailEntranceSet = new Set(tail.map(s => s.entrance_node_id))
    const trimAtTailEntrance = (segs: RouteSegment[]) => {
      for (let i = 0; i < segs.length - 1; i++) {
        if (tailEntranceSet.has(segs[i]!.exit_node_id)) return segs.slice(0, i + 1)
      }
      return segs
    }
    if (!addTarget) {
      const tailSegment = tail.find(s => s.entrance_node_id === lastNodeId)
      if (tailSegment) return [tailSegment]
      const targets = [...new Set(tail.map(s => s.entrance_node_id))]
      const segs = findShortestPathToFirstReachable(routingGraph, lastNodeId, targets)
      return !isEmpty(segs) ? trimAtTailEntrance(segs!) : null
    }
    if (lastNodeId === addTarget) return null
    const segments = findShortestPath(routingGraph, lastNodeId, addTarget)
    return !isEmpty(segments) ? trimAtTailEntrance(segments!) : null
  }, [isDeleteMode, removalPlan, editState, pathState.lastNode, addTarget, routingGraph])

  const hintReconnectNodeId =
    isPathBuildMode && !isEmpty(shortestPathHint) ? last(shortestPathHint)!.exit_node_id : null

  const hintVisitByNode = useMemo(
    () => buildHintVisitNumbers(isPathBuildMode ? shortestPathHint : null, pathNodes.length),
    [shortestPathHint, isPathBuildMode, pathNodes.length]
  )

  const hintReconnectPosition = useMemo(
    () => (isPathBuildMode ? hintReconnectVisitNumber(shortestPathHint, pathNodes.length, hintVisitByNode) : undefined),
    [isPathBuildMode, shortestPathHint, pathNodes.length, hintVisitByNode]
  )

  const hintNodeIds = useMemo(() => {
    if (isEmpty(shortestPathHint) || !isPathBuildMode) return new Set<string>()
    const ids = new Set<string>()
    const reconnectNodeId = last(shortestPathHint)!.exit_node_id
    const sourceNodeId = shortestPathHint![0]!.entrance_node_id
    for (const seg of shortestPathHint!) {
      if (seg.entrance_node_id !== sourceNodeId && seg.entrance_node_id !== reconnectNodeId) {
        ids.add(seg.entrance_node_id)
      }
      if (seg.exit_node_id !== sourceNodeId && seg.exit_node_id !== reconnectNodeId) {
        ids.add(seg.exit_node_id)
      }
    }
    return ids
  }, [shortestPathHint, isPathBuildMode])

  const pendingNodeIds = useMemo(() => {
    if (!pendingRemoval) return new Set<string>()
    return new Set(pathNodes.slice(pendingRemoval.rangeStart, pendingRemoval.rangeEnd + 1))
  }, [pendingRemoval, pathNodes])

  const routeGapNodeIds = useMemo(
    () => detectRouteGapNodeIds(pathState.segments, pathState.lastNode, indexedSegments, streetNodes),
    [pathState.segments, pathState.lastNode, indexedSegments, streetNodes]
  )

  const gapFixes: GapFix[] = useMemo(
    () => computeGapFixes(pathState.segments, indexedSegments, streetNodes, routingGraph),
    [pathState.segments, indexedSegments, streetNodes, routingGraph]
  )

  useEffect(() => {
    if (isEmpty(gapFixes)) setGapFixPreview(false)
  }, [gapFixes, setGapFixPreview])

  const reconnectIndex = useMemo(() => {
    if (!editState || !pathState.lastNode) return 0
    const idx = findTailReconnectIndex(editState.originalTail, pathState.lastNode)
    return idx ?? editState.originalTail.length
  }, [editState, pathState.lastNode])

  return {
    removalPlan,
    shortestPathHint,
    hintReconnectNodeId,
    hintVisitByNode,
    hintReconnectPosition,
    hintNodeIds,
    pendingNodeIds,
    routeGapNodeIds,
    gapFixes,
    reconnectIndex,
  }
}
